<template>
  <div>
    <b-sidebar
      id="sidebar-backdrop"
      backdrop
      shadow
      bg-variant="white"
      sidebar-class="sidebar-lg"
      no-header
      right
      :visible="sidebarActive"
      @change="(val) => $emit('update:sidebar-active', val)"
    >
      <template #default="{ hide }">
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
          <h5 class="mb-0">
            {{ isEditing ? 'Editar' : 'Nuevo' }} Color
          </h5>
          <feather-icon
            class="ml-1 cursor-pointer"
            icon="XIcon"
            size="16"
            @click="hide"
          />
        </div>
        <!-- Campos formulario -->
        <validation-observer ref="simpleRules">
          <b-form
            v-if="show"
            class="p-2"
            @submit="onSubmit"
            @reset="onReset"
          >
            <!-- Nombre -->
            <validation-provider
              v-slot="{ errors }"
              name="nombre"
              rules="required"
            >
              <b-form-group
                label="Nombre:"
                label-for="input-name"
              >
                <b-form-input
                  id="input-name"
                  v-model="form.name"
                  autocomplete="new-password"
                  placeholder="Ingresa el nombre del color"
                  required
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <!-- Código Externo -->
            <validation-provider
              v-slot="{ errors }"
              name="código externo"
            >
              <b-form-group
                label="Código Externo:"
                label-for="input-external-code"
              >
                <b-form-input
                  id="input-external-code"
                  v-model="form.external_code"
                  placeholder="Ingresa el código externo (opcional)"
                />
                <small class="text-muted">Código para integración con sistemas externos</small>
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <!-- Color Picker -->
            <validation-provider
              v-slot="{ errors }"
              name="color"
            >
              <b-form-group
                label="Color:"
                label-for="input-color"
              >
                <div class="d-flex align-items-center">
                  <input
                    id="input-color"
                    v-model="form.hex_code"
                    type="color"
                    class="form-control color-picker mr-2"
                    style="width: 60px; height: 40px; padding: 2px;"
                    @input="onColorChange"
                  >
                  <b-form-input
                    v-model="form.hex_code"
                    placeholder="#000000"
                    class="flex-grow-1"
                    @input="onHexInput"
                  />
                </div>
                <small class="text-muted">Selecciona un color o ingresa el código hexadecimal</small>
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <!-- Vista previa del color -->
            <b-form-group
              v-if="form.hex_code"
              label="Vista Previa:"
            >
              <div class="d-flex align-items-center">
                <div
                  class="color-preview-large mr-2"
                  :style="{ backgroundColor: form.hex_code }"
                />
                <span class="text-muted">{{ form.hex_code }}</span>
              </div>
            </b-form-group>

            <!-- Estado -->
            <validation-provider
              v-slot="{ errors }"
              name="estado"
              rules="required"
            >
              <b-form-group
                label="Estado:"
                label-for="select-status"
              >
                <v-select
                  v-model="form.status"
                  :options="statusOptions"
                  :reduce="status => status.value"
                  :clearable="false"
                  placeholder="Selecciona el estado"
                  input-id="select-status"
                  required
                >
                  <template slot="no-options">
                    Lo siento, no se encontraron opciones
                  </template>
                </v-select>
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <b-button
              type="submit"
              variant="primary"
            >
              {{ isEditing ? 'Actualizar' : 'Crear' }}
            </b-button>
            <b-button
              style="margin-left: 5px;"
              type="reset"
              variant="secondary"
            >
              {{ isEditing ? 'Restaurar' : 'Limpiar' }}
            </b-button>
          </b-form>
        </validation-observer>
      </template>

    </b-sidebar>
  </div>
</template>

<script>
import axios from '@axios'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import { required } from '@/utils/validations/validations'

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    vSelect,
  },
  model: {
    prop: 'sidebarActive',
    event: 'update:sidebar-active',
  },
  props: {
    sidebarActive: {
      type: Boolean,
      default: false,
    },
    colorId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      required,
      show: true,
      form: {
        name: '',
        hex_code: '',
        external_code: '',
        status: 'Y',
      },
      originalForm: {
        name: '',
        hex_code: '',
        external_code: '',
        status: 'Y',
      },
      statusOptions: [
        { label: 'Activo', value: 'Y' },
        { label: 'Inactivo', value: 'N' },
      ],
    }
  },
  computed: {
    isEditing() {
      return this.colorId !== null && this.colorId !== undefined && this.colorId > 0
    },
  },
  watch: {
    sidebarActive(newVal) {
      if (newVal && this.colorId) {
        this.getColor()
      } else if (newVal && !this.colorId) {
        this.resetForm()
      }
    },
  },
  methods: {
    async getColor() {
      try {
        const response = await axios.get(`color/${this.colorId}`)
        const color = response.data.data

        this.form = {
          name: color.name,
          hex_code: color.hex_code || '',
          external_code: color.external_code || '',
          status: color.status,
        }

        this.originalForm = { ...this.form }
      } catch (error) {
        console.error('Error fetching color:', error)
        this.makeToast('danger', 'Error', 'Error al cargar el color')
        this.$emit('update:sidebar-active', false)
      }
    },

    onColorChange(event) {
      this.form.hex_code = event.target.value
    },

    onHexInput(value) {
      // Validar formato hexadecimal
      if (value && !/^#[0-9A-F]{6}$/i.test(value)) {
        // Si no empieza con #, agregarlo
        if (!value.startsWith('#')) {
          this.form.hex_code = `#${value}`
        }
      }
    },

    async onSubmit(event) {
      event.preventDefault()

      try {
        const isValid = await this.$refs.simpleRules.validate()

        if (!isValid) {
          return
        }

        // Validar formato hexadecimal si se proporciona
        if (this.form.hex_code && !/^#[0-9A-F]{6}$/i.test(this.form.hex_code)) {
          this.makeToast('danger', 'Error', 'El código de color debe tener formato hexadecimal válido (ej: #FF0000)')
          return
        }

        let response
        if (this.isEditing) {
          response = await axios.put(`color/${this.colorId}`, this.form)
        } else {
          response = await axios.post('color', this.form)
        }

        this.makeToast('success', 'Éxito', response.data.message || (this.isEditing ? 'Color actualizado exitosamente' : 'Color creado exitosamente'))

        this.resetForm()
        this.$emit('refreshData')
        this.$emit('update:sidebar-active', false)
      } catch (error) {
        console.error('Error saving color:', error)
        let errorMessage = this.isEditing ? 'Error al actualizar el color' : 'Error al crear el color'

        if (error.response && error.response.data && error.response.data.errors) {
          const { errors } = error.response.data
          errorMessage = Object.values(errors).flat().join(', ')
        } else if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message
        }

        this.makeToast('danger', 'Error', errorMessage)
      }
    },

    onReset(event) {
      event.preventDefault()
      if (this.isEditing) {
        this.form = { ...this.originalForm }
      } else {
        this.resetForm()
      }
      this.$refs.simpleRules.reset()
    },

    resetForm() {
      this.form = {
        name: '',
        hex_code: '',
        external_code: '',
        status: 'Y',
      }
      this.originalForm = { ...this.form }
    },
  },
}
</script>

<style lang="scss" scoped>
.content-sidebar-header {
  border-bottom: 1px solid #d8d6de;
}

.color-picker {
  border: 1px solid #d8d6de;
  border-radius: 0.357rem;
  cursor: pointer;
}

.color-preview-large {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  border: 2px solid #d8d6de;
}
</style>
